import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

/* CSS */
import styles from './HomePage.module.css';
import shared from '@/styles/shared.module.css';

/* Card components */
import TripCard from '@/components/trips/TripCard';
import StatCard from '@/components/cards/StatCard';

/* Context */
import { useAuthContext } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';

/* Icons */
import countries_visited from '@/assets/icons/countries_visited.svg';
import days_traveled from '@/assets/icons/days_traveled.svg';
import itineraries_planned from '@/assets/icons/itineraries_planned.svg';

import search_icon from '@/assets/icons/search_icon.svg';

import swipe_right_clicked from '@/assets/icons/swipe_right_clicked.svg';
import swipe_left_clicked from '@/assets/icons/swipe_left_clicked.svg';
import swipe_right_unclicked from '@/assets/icons/swipe_right_unclicked.svg';
import swipe_left_unclicked from '@/assets/icons/swipe_left_unclicked.svg';

import add_clicked from '@/assets/icons/add_clicked.svg'
import add_unclicked from '@/assets/icons/add_unclicked.svg'

/* Stat details */
import TripsPlannedDetail from '@/components/cards/statDetails/TripsPlannedDetail';
import DaysTraveledDetail from '@/components/cards/statDetails/DaysTraveledDetail';
import CountriesDetail from '@/components/cards/statDetails/CountriesDetail';

/* Utils */
import { filterTrips } from '@/utils/filterUtils';
import { splitTripsByDate } from '@/utils/dateUtils';
import { calculateTravelStats, calculateTravelStatsDetailed } from '@/utils/statsUtils';
import { tripUtils } from '@/utils/tripUtils';

const HomePage = () => {

    /* Prep work */
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [isNextHover, setIsNextHover] = useState(false);
    const [isPrevHover, setIsPrevHover] = useState(false);

    const [allTrips, setAllTrips] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [homeStats, setHomeStats] = useState(null);
    const [detailed, setDetailed] = useState(null);

    const { currentUser } = useAuthContext();
    const { showToast } = useToast();

    const viewportRef = React.useRef(null);

    const { upcoming, _ } = splitTripsByDate(allTrips);
    const filteredUpcoming = filterTrips(upcoming, searchQuery);

    // Calculate total pages dynamically based on scroll width vs client width
    const [totalPages, setTotalPages] = useState(1);

    // Update total pages whenever data changes or window resizes
    useEffect(() => {
        const updatePages = () => {
            if (viewportRef.current) {
                const { scrollWidth, clientWidth } = viewportRef.current;
                // Add explicit check: if scrollWidth is roughly same as clientWidth, it's 1 page
                if (scrollWidth <= clientWidth + 5) { // 5px tolerance
                    setTotalPages(1);
                } else {
                    setTotalPages(Math.ceil(scrollWidth / clientWidth));
                }
            }
        };

        // Run initially and on updates
        updatePages();

        // Listen for resize
        window.addEventListener('resize', updatePages);
        return () => window.removeEventListener('resize', updatePages);
    }, [filteredUpcoming, allTrips]);

    /* Navigation */
    const navigate = useNavigate();
    const handleCreateNewTrip = () => {
        navigate('/new', { state: { from: '/' } });
    };

    /* Update page state on scroll */
    const handleScroll = () => {
        if (viewportRef.current) {
            const { scrollLeft, clientWidth, scrollWidth } = viewportRef.current;

            // Check if we are at the end (within small tolerance)
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

            if (isAtEnd) {
                // Force last page
                const total = Math.ceil(scrollWidth / clientWidth);
                setCurrentPage(total - 1);
            } else {
                // Standard rounding
                const newPage = Math.round(scrollLeft / clientWidth);
                setCurrentPage(newPage);
            }
        }
    };

    /* Carousel navigation handlers */
    const scrollToIndex = (index) => {
        if (viewportRef.current) {
            const width = viewportRef.current.clientWidth;
            viewportRef.current.scrollTo({
                left: index * width,
                behavior: 'smooth'
            });
        }
    };

    const goNextPage = () => {
        scrollToIndex(currentPage + 1);
        setIsNextHover(false);
    };

    const goPrevPage = () => {
        scrollToIndex(currentPage - 1);
        setIsPrevHover(false);
    };

    /* Render */
    useEffect(() => {
        const fetchTrips = async () => {
            try {
                const storedTrips = await tripUtils.getAllTrips();
                setAllTrips(storedTrips);

                const stats = calculateTravelStats(storedTrips);
                setHomeStats(stats);
                setDetailed(calculateTravelStatsDetailed(storedTrips, currentUser));
            } catch (error) {
                console.error("Error fetching trips:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTrips();
    }, []);

    const handleDeleteTrip = async (tripId) => {
        try {
            await tripUtils.deleteTrip(tripId);
            setAllTrips(prev => prev.filter(t => t.id !== tripId));
            showToast('Trip deleted', 'success');
        } catch (error) {
            console.error("Failed to delete trip", error);
            showToast('Could not delete trip. Please try again.', 'error');
        }
    };

    const handleDuplicateTrip = async (tripId) => {
        try {
            const newTrip = await tripUtils.duplicateTrip(tripId);
            setAllTrips(prev => [newTrip, ...prev]);
            showToast('Trip duplicated', 'success');
        } catch (error) {
            console.error("Failed to duplicate trip", error);
            showToast('Could not duplicate trip. Please try again.', 'error');
        }
    };

    // Determine when to show arrows
    const showPrev = currentPage > 0;
    const showNext = currentPage < totalPages - 1;

    if (isLoading) {
        return <div className={shared.mainContent}>Loading your adventures...</div>;
    }

    return (
        <div>
            {/* Page header */}
            <section className={shared.heroHeader}>
                {/* Page title */}
                <h1 className={shared.heroTitle}>Welcome, {currentUser?.username}!</h1>
                <p className={shared.heroSubtitle}>Ready to plan your next adventure?</p>

                {/* Search bar */}
                <div className={shared.searchBar}>
                    <span>
                        <img
                            src={search_icon}
                            alt="Search Icon"
                            className={shared.searchIcon}
                        />
                    </span>
                    <input
                        type="text"
                        placeholder="Quick trip lookup..."
                        aria-label="Search trips"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </section>

            {/* Upcoming trips */}
            <section className={shared.section}>

                {/* Section header */}
                <div className={shared.sectionHeader}>
                    <h2 className={shared.sectionTitle}>Upcoming Trips ({filteredUpcoming.length})</h2>
                    <Link to="/trips" className={styles.linkButton}>All trips &gt;</Link>
                </div>
                <p className={shared.sectionSubtitle}>Your next adventures await!</p>

                {/* Content or empty state */}
                {filteredUpcoming.length === 0 ? (
                    <div>
                        {/* Create new trip */}
                        <button
                            type='button'
                            className={styles.newTripBtn}
                            onClick={handleCreateNewTrip}
                        >
                            <img
                                src={add_unclicked}
                                alt="Create new trip"
                                className={styles.homepageNewTripIcon}
                                onMouseEnter={e => e.currentTarget.src = add_clicked}
                                onMouseLeave={e => e.currentTarget.src = add_unclicked}
                            />
                        </button>

                        <p className={shared.emptyState}>
                            {upcoming.length === 0
                                ? "When you plan a new trip, it will show up here."
                                : "No trips match your search."}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Carousel wrapper */}
                        <div className={styles.cardCarousel}>
                            <div
                                className={styles.cardCarouselViewport}
                                ref={viewportRef}
                                onScroll={handleScroll}
                            >
                                <div className={styles.cardRowCarousel}>
                                    {filteredUpcoming.map((trip) => (
                                        <div key={trip.id} className={styles.carouselItem}>
                                            <TripCard
                                                trip={trip}
                                                onDelete={handleDeleteTrip}
                                                onDuplicate={handleDuplicateTrip}
                                            />
                                        </div>
                                    ))}

                                    {/* Create new trip */}
                                    <button
                                        type='button'
                                        className={styles.newTripBtn}
                                        onClick={handleCreateNewTrip}
                                    >
                                        <img
                                            src={add_unclicked}
                                            alt="Create new trip"
                                            className={styles.homepageNewTripIcon}
                                            onMouseEnter={e => e.currentTarget.src = add_clicked}
                                            onMouseLeave={e => e.currentTarget.src = add_unclicked}
                                        />
                                    </button>
                                </div>
                            </div>

                            {/* Previous button */}
                            {showPrev && (
                                <button
                                    type="button"
                                    className={styles.carouselPrevButton}
                                    onClick={goPrevPage}
                                    onMouseEnter={() => setIsPrevHover(true)}
                                    onMouseLeave={() => setIsPrevHover(false)}
                                    aria-label="Previous trips"
                                >
                                    <img
                                        src={isPrevHover ? swipe_left_clicked : swipe_left_unclicked}
                                        alt="Previous trips"
                                        className={styles.carouselPrevIcon}
                                    />
                                </button>
                            )}

                            {/* Next button */}
                            {showNext && (
                                <button
                                    type="button"
                                    className={styles.carouselNextButton}
                                    onClick={goNextPage}
                                    onMouseEnter={() => setIsNextHover(true)}
                                    onMouseLeave={() => setIsNextHover(false)}
                                    aria-label="Next trips"
                                >
                                    <img
                                        src={isNextHover ? swipe_right_clicked : swipe_right_unclicked}
                                        alt="Next trips"
                                        className={styles.carouselNextIcon}
                                    />
                                </button>
                            )}
                        </div>

                        {/* Pagination dots */}
                        {totalPages > 1 && (
                            <div className={styles.carouselDots}>
                                {Array.from({ length: totalPages }).map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`${styles.carouselDot} ${index === currentPage ? styles.active : ''}`}
                                        onClick={() => scrollToIndex(index)}
                                        aria-label={`Go to page ${index + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* Travel stats */}
            <section className={shared.section}>
                <div className={shared.sectionHeader}>
                    <h2 className={shared.sectionTitle}>Your Travel Stats</h2>
                    <Link to="/summary" className={styles.linkButton}>All stats &gt;</Link>
                </div>
                <p className={shared.sectionSubtitle}>Look how much you've traveled!</p>

                <div className={styles.statGrid}>
                    <StatCard
                        label="Countries Visited"
                        value={homeStats ? homeStats.totalCountries : '-'}
                        icon={countries_visited}
                        info="Number of countries you have visited"
                        back={detailed && <CountriesDetail countries={detailed.countries} />}
                    />
                    <StatCard
                        label="Days Spent Travelling"
                        value={homeStats ? homeStats.totalDays : '-'}
                        icon={days_traveled}
                        info="Total number of days you have spent traveling"
                        iconStyle={{ marginLeft: '-4px' }}
                        back={detailed && <DaysTraveledDetail rows={detailed.perTripDays} total={detailed.totalDaysClamped} />}
                    />
                    <StatCard
                        label="Trips Planned"
                        value={homeStats ? homeStats.totalTrips : '-'}
                        icon={itineraries_planned}
                        info="Number of trips you have planned"
                        back={detailed && <TripsPlannedDetail trips={detailed.tripsPlanned} />}
                    />
                </div>
                <p className={styles.homepageSubtitle}>Keep exploring, TravelBuddy is with you!</p>
            </section>
        </div>
    );
};

export default HomePage;
